"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/tailwind";
import CloseIcon from "@/assets/icons/close.svg?react";
import ChevronLeftIcon from "@/assets/icons/chevronLeft.svg";
import ChevronRightIcon from "@/assets/icons/chevronRight.svg";
import Image from "next/image";
import { TranslationData } from "@/common/lib/types";
import { FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { getCurrencyByLocale, getCurrencyFontClass, translateFilterLabel } from "@/apiUtils/commonUtils";
import { facetTypeConstant } from "@/apiUtils/constants";
import { useParams } from "next/navigation";

export interface IProps {
  appliedFilters: Record<string, string>;
  filters: FilteredFacet[];
  lang: string;
  className?: string;
  clearAll?: boolean;
  translations: TranslationData;
  onRemove: (key: string, value: string, all?: boolean) => void;
}

const SelectedFilters = ({
  appliedFilters,
  filters,
  lang,
  className = "",
  clearAll = false,
  translations,
  onRemove,
}: IProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const params = useParams();
  const locale = params?.locale as string;
  const currencyFontClass = getCurrencyFontClass(getCurrencyByLocale(locale));

  const cleanedParams = useMemo(() => {
    const temp = { ...appliedFilters };
    delete temp["_p-l"];
    delete temp["q"];
    return temp;
  }, [appliedFilters]);

  const hasAppliedParams = useMemo(
    () => Object.keys(cleanedParams).length > 0,
    [cleanedParams],
  );

  const checkScroll = () => {
    if (scrollRef.current) {
      setCanScrollLeft(scrollRef.current.scrollLeft > 0);
      setCanScrollRight(
        scrollRef.current.scrollLeft <
          scrollRef.current.scrollWidth - scrollRef.current.clientWidth,
      );
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -150 : 150,
        behavior: "smooth",
      });
    }
  };

  // Find filter purely by filterParams URL key — no static map lookup
  const findFilterByKey = (key: string): FilteredFacet | undefined =>
    filters?.find((item) => item?.filterParams === key);

  const getDisplayValue = (key: string, val: string) => {
      const matched = findFilterByKey(key);

      if (["true", "false"].includes(val)) {
        // Boolean-group children carry their own filterParams, so the chip must
        // resolve the child label — not the group name, and never the raw key.
        const booleanChild = filters
          ?.flatMap((item) => item?.booleanMappings || [])
          ?.find((mapping) => mapping?.filterParams === key);
        return booleanChild?.label || matched?.name?.[lang] || key;
      }

     
      if (matched?.facetAttributeEn?.includes("clPerPackSize")) {
        return translateFilterLabel(lang, val, facetTypeConstant.PACK_FILTER);
      }

      if (lang === "ar") {
        const mappings = matched?.facetMapping || [];
        const arValue = mappings?.find((item) => item?.value === val)?.label;
        if (arValue) return arValue;
      }

      return val;
    };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  const handleRemoveSearchValue = (key: string, value: string) => {
    onRemove(key, value);
  };

  const isPriceFilter = (key: string) => key === "p";

  if (!hasAppliedParams) return <></>;

  return (
    <div className={cn("flex w-full items-center bg-tertiary-5 py-3", className)}>
      {clearAll && canScrollLeft && (
        <button
          className="me-2 flex items-center justify-center p-2"
          onClick={() => scroll("left")}
        >
          <Image src={ChevronLeftIcon} height={20} alt="left" />
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="no-scrollbar flex max-w-full flex-shrink items-center overflow-x-auto"
      >
        {Object.entries(cleanedParams).map(([key, valueStr], index) => {
          const values = valueStr?.split("|");
          return values?.map((val, idx) => {
            const displayValue = getDisplayValue(key, val);
            return (
              <div
                key={idx}
                className={`${index === 0 ? "ms-2" : ""} me-2 flex w-auto items-center whitespace-nowrap rounded-md bg-primary-4 px-4 py-2`}
              >
                <p className={cn("text-sm", isPriceFilter(key) && currencyFontClass)}>
                  {isPriceFilter(key) ? <bdi>{displayValue}</bdi> : displayValue}
                </p>
                <button type="button" className="ms-2">
                  <CloseIcon
                    className="w-3"
                    onClick={() => handleRemoveSearchValue(key, val)}
                  />
                </button>
              </div>
            );
          });
        })}
      </div>

      {clearAll && canScrollRight && (
        <button
          className="ms-2 flex items-center justify-center p-2"
          onClick={() => scroll("right")}
        >
          <Image src={ChevronRightIcon} height={20} alt="right" />
        </button>
      )}

      {clearAll && (
        <button
          className="ms-2 flex-shrink-0 text-sm font-semibold"
          onClick={() => onRemove("", "", true)}
        >
          {translations?.plp?.plp_filter_option_clear_all_label || ""}
        </button>
      )}
    </div>
  );
};

export default SelectedFilters;